/**
 * Turns a pathname into the head tags that page should carry.
 *
 * Pure and DOM-free on purpose: the Vite plugin imports it under Node to bake
 * tags into the HTML a crawler receives, and `useDocumentMeta` imports it in the
 * browser to keep them right after a client-side navigation. One renderer, so
 * the two can never drift.
 */
import { NOT_FOUND_META, PRIVATE_PAGES, SITE_ROUTES, SPA_SHELL_META, type PageMeta } from './routes';

export const SITE_NAME = 'VecTime Cloud';

/** Share image for a page that names none of its own. 1200×630. */
const OG_IMAGE_PATH = '/brand/og-image.png';
const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';

/** Where the feed lives, and what readers call it. */
export const FEED_PATH = '/feed.xml';
const FEED_TITLE = 'VecTime Cloud — forecasting benchmarks and notes';

const BY_PATH: ReadonlyMap<string, PageMeta> = new Map(
  [
    // Drop the sitemap-only fields; everything left is page meta.
    ...SITE_ROUTES.map(({ source, changefreq, priority, ...meta }) => meta),
    ...PRIVATE_PAGES,
  ].map(({ path, ...meta }) => [path, meta]),
);

/**
 * Paths that get their own prerendered HTML file at build time.
 *
 * The dashboard is excluded: it needs a session, so nobody unfurls it and a
 * crawler never gets past the login redirect — `useDocumentMeta` handles its
 * browser tab. Everything else falls back to index.html, which is what the 404
 * route wants anyway.
 */
export const PRERENDER_PATHS: readonly string[] = [...BY_PATH.keys()].filter(
  (path) => !path.startsWith('/app'),
);

/** `/docs/`, `/docs?x=1` and `/docs` are one page; `/` stays `/`. */
export function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]!.split('#')[0]!.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

/** Meta for a path, falling back to the 404's. */
export function resolvePageMeta(pathname: string): PageMeta {
  return BY_PATH.get(normalizePath(pathname)) ?? NOT_FOUND_META;
}

export interface TagSpec {
  tag: 'meta' | 'link';
  attrs: Readonly<Record<string, string>>;
}

export interface PageTags {
  /** Text for `<title>`. */
  title: string;
  /** Everything else in the head this module owns. */
  tags: readonly TagSpec[];
}

/**
 * @param url absolute URL of the page, or null when it has no single URL — the
 *   shell document, which any un-prerendered path is served
 */
function buildTags(meta: PageMeta, url: string | null, siteUrl: string): PageTags {
  const image = `${siteUrl}${meta.image ?? OG_IMAGE_PATH}`;

  const tags: TagSpec[] = [
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
    ...(url
      ? [
          { tag: 'link' as const, attrs: { rel: 'canonical', href: url } },
          { tag: 'meta' as const, attrs: { property: 'og:url', content: url } },
        ]
      : []),
    { tag: 'meta', attrs: { property: 'og:type', content: meta.ogType ?? 'website' } },
    { tag: 'meta', attrs: { property: 'og:site_name', content: SITE_NAME } },
    // Dates belong to og:type=article and are ignored elsewhere, so they are
    // emitted only where they mean something rather than on every page.
    ...(meta.ogType === 'article' && meta.publishedTime
      ? [{ tag: 'meta' as const, attrs: { property: 'article:published_time', content: meta.publishedTime } }]
      : []),
    ...(meta.ogType === 'article' && meta.modifiedTime
      ? [{ tag: 'meta' as const, attrs: { property: 'article:modified_time', content: meta.modifiedTime } }]
      : []),
    { tag: 'meta', attrs: { property: 'og:title', content: meta.title } },
    { tag: 'meta', attrs: { property: 'og:description', content: meta.description } },
    { tag: 'meta', attrs: { property: 'og:image', content: image } },
    { tag: 'meta', attrs: { property: 'og:image:width', content: OG_IMAGE_WIDTH } },
    { tag: 'meta', attrs: { property: 'og:image:height', content: OG_IMAGE_HEIGHT } },
    { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attrs: { name: 'twitter:title', content: meta.title } },
    { tag: 'meta', attrs: { name: 'twitter:description', content: meta.description } },
    { tag: 'meta', attrs: { name: 'twitter:image', content: image } },
    // Announced on every page, not just the blog: a reader's discovery button
    // looks at whatever tab happens to be open.
    {
      tag: 'link',
      attrs: {
        rel: 'alternate',
        type: 'application/rss+xml',
        title: FEED_TITLE,
        href: `${siteUrl}${FEED_PATH}`,
      },
    },
  ];

  if (meta.noindex) {
    tags.push({ tag: 'meta', attrs: { name: 'robots', content: 'noindex, nofollow' } });
  }

  return { title: meta.title, tags };
}

/**
 * @param pathname route path, with or without a trailing slash
 * @param siteUrl origin without a trailing slash, e.g. `https://vectime.cloud`
 */
export function pageTags(pathname: string, siteUrl: string): PageTags {
  const path = normalizePath(pathname);
  // Root stays bare — `https://vectime.cloud/` rather than an empty path.
  const url = path === '/' ? `${siteUrl}/` : `${siteUrl}${path}`;
  return buildTags(resolvePageMeta(path), url, siteUrl);
}

/** Tags for the shell document, which stands in for every path not prerendered. */
export function shellTags(siteUrl: string): PageTags {
  return buildTags(SPA_SHELL_META, null, siteUrl);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The tags as HTML, ready to drop into `<head>`.
 *
 * Every tag is stamped `data-seo` so the client can swap the whole block on
 * navigation instead of appending a second copy of it.
 */
export function renderTags({ title, tags }: PageTags, indent = '    '): string {
  const lines = [
    `<title>${escapeHtml(title)}</title>`,
    ...tags.map(({ tag, attrs }) => {
      const rendered = Object.entries(attrs)
        .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
        .join(' ');
      return `<${tag} ${rendered} data-seo />`;
    }),
  ];
  return lines.map((line) => `${indent}${line}`).join('\n');
}
